import React, { useState, useEffect } from 'react';
import './Arbitrage.css';

const Arbitrage = () => {
  const [arbitrageOpportunities, setArbitrageOpportunities] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [selectedExchanges, setSelectedExchanges] = useState(['Binance', 'Coinbase', 'Kraken', 'KuCoin']);
  const [minProfitThreshold, setMinProfitThreshold] = useState(0.5);
  const [maxAmount, setMaxAmount] = useState(1000);
  const [arbitrageProfits, setArbitrageProfits] = useState({
    totalProfit: 0,
    dailyProfit: 0,
    executedTrades: 0,
    successRate: 0
  });

  // Simulate arbitrage scanning
  const startArbitrageScan = () => {
    setIsScanning(true);
    setScanProgress(0);
    setArbitrageOpportunities([]);

    const interval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsScanning(false);
          generateArbitrageOpportunities();
          return 100;
        }
        return prev + 2;
      });
    }, 100);
  };

  const generateArbitrageOpportunities = () => {
    const opportunities = [
      {
        id: 1,
        pair: 'BTC/USDT',
        buyExchange: 'Binance',
        sellExchange: 'Coinbase',
        buyPrice: 43250.50,
        sellPrice: 43320.80,
        profit: 70.30,
        profitPercent: 0.16,
        maxAmount: 500,
        timestamp: new Date().toISOString(),
        status: 'active'
      },
      {
        id: 2,
        pair: 'ETH/USDT',
        buyExchange: 'Kraken',
        sellExchange: 'KuCoin',
        buyPrice: 2650.25,
        sellPrice: 2665.80,
        profit: 15.55,
        profitPercent: 0.59,
        maxAmount: 200,
        timestamp: new Date().toISOString(),
        status: 'active'
      },
      {
        id: 3,
        pair: 'SOL/USDT',
        buyExchange: 'Coinbase',
        sellExchange: 'Binance',
        buyPrice: 98.45,
        sellPrice: 99.20,
        profit: 0.75,
        profitPercent: 0.76,
        maxAmount: 1000,
        timestamp: new Date().toISOString(),
        status: 'active'
      }
    ];

    setArbitrageOpportunities(opportunities);
  };

  const executeArbitrage = (opportunityId) => {
    setArbitrageOpportunities(prev => 
      prev.map(opp => 
        opp.id === opportunityId 
          ? { ...opp, status: 'executing' }
          : opp
      )
    );

    // Simulate execution delay
    setTimeout(() => {
      setArbitrageOpportunities(prev => 
        prev.map(opp => {
          if (opp.id === opportunityId) {
            const completedOpp = { ...opp, status: 'completed' };
            
            // Update profit tracking
            setArbitrageProfits(prev => ({
              totalProfit: prev.totalProfit + opp.profit,
              dailyProfit: prev.dailyProfit + opp.profit,
              executedTrades: prev.executedTrades + 1,
              successRate: ((prev.executedTrades + 1) / (prev.executedTrades + 1)) * 100
            }));
            
            return completedOpp;
          }
          return opp;
        })
      );
    }, 2000);
  };

  return (
    <div className="arbitrage-page">
      {/* Header */}
      <div className="arbitrage-header">
        <h2>Arbitrage Scanner</h2>
        <p>Find profitable price differences across exchanges</p>
      </div>

      {/* Control Panel */}
      <div className="control-panel">
        <div className="control-section">
          <h3>Exchange Selection</h3>
          <div className="exchange-grid">
            {['Binance', 'Coinbase', 'Kraken', 'KuCoin', 'Bybit', 'OKX'].map(exchange => (
              <label key={exchange} className="exchange-checkbox">
                <input
                  type="checkbox"
                  checked={selectedExchanges.includes(exchange)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedExchanges(prev => [...prev, exchange]);
                    } else {
                      setSelectedExchanges(prev => prev.filter(ex => ex !== exchange));
                    }
                  }}
                />
                <span>{exchange}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="control-section">
          <h3>Scan Parameters</h3>
          <div className="parameter-grid">
            <div className="parameter-item">
              <label>Min Profit Threshold (%)</label>
              <input
                type="number"
                value={minProfitThreshold}
                onChange={(e) => setMinProfitThreshold(parseFloat(e.target.value))}
                step="0.1"
                min="0"
                max="10"
              />
            </div>
            <div className="parameter-item">
              <label>Max Amount ($)</label>
              <input
                type="number"
                value={maxAmount}
                onChange={(e) => setMaxAmount(parseInt(e.target.value))}
                min="100"
                max="10000"
              />
            </div>
          </div>
        </div>

        <div className="control-section">
          <button 
            className={`scan-btn ${isScanning ? 'scanning' : ''}`}
            onClick={startArbitrageScan}
            disabled={isScanning}
          >
            {isScanning ? `Scanning... ${scanProgress}%` : 'Start Arbitrage Scan'}
          </button>
        </div>
      </div>

      {/* Scan Progress */}
      {isScanning && (
        <div className="scan-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${scanProgress}%` }}
            ></div>
          </div>
          <p>Scanning {selectedExchanges.length} exchanges for opportunities...</p>
        </div>
      )}

      {/* Arbitrage Opportunities */}
      <div className="opportunities-section">
        <h3>Arbitrage Opportunities</h3>
        {arbitrageOpportunities.length === 0 ? (
          <div className="no-opportunities">
            <div className="no-opportunities-icon">🔍</div>
            <p>No arbitrage opportunities found</p>
            <p>Click "Start Arbitrage Scan" to search for profitable trades</p>
          </div>
        ) : (
          <div className="opportunities-grid">
            {arbitrageOpportunities.map(opportunity => (
              <div key={opportunity.id} className={`opportunity-card ${opportunity.status}`}>
                <div className="opportunity-header">
                  <div className="pair-info">
                    <h4>{opportunity.pair}</h4>
                    <span className="profit-badge">
                      +{opportunity.profitPercent.toFixed(2)}%
                    </span>
                  </div>
                  <div className={`status-indicator ${opportunity.status}`}>
                    {opportunity.status === 'active' && '🟢'}
                    {opportunity.status === 'executing' && '🟡'}
                    {opportunity.status === 'completed' && '✅'}
                  </div>
                </div>

                <div className="opportunity-details">
                  <div className="price-comparison">
                    <div className="price-item buy">
                      <span className="exchange">{opportunity.buyExchange}</span>
                      <span className="price">${opportunity.buyPrice.toFixed(2)}</span>
                      <span className="label">Buy</span>
                    </div>
                    <div className="arrow">→</div>
                    <div className="price-item sell">
                      <span className="exchange">{opportunity.sellExchange}</span>
                      <span className="price">${opportunity.sellPrice.toFixed(2)}</span>
                      <span className="label">Sell</span>
                    </div>
                  </div>

                  <div className="profit-info">
                    <div className="profit-item">
                      <span className="label">Profit:</span>
                      <span className="value profit">+${opportunity.profit.toFixed(2)}</span>
                    </div>
                    <div className="profit-item">
                      <span className="label">Max Amount:</span>
                      <span className="value">${opportunity.maxAmount}</span>
                    </div>
                    <div className="profit-item">
                      <span className="label">Timestamp:</span>
                      <span className="value">{new Date(opportunity.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>

                <div className="opportunity-actions">
                  {opportunity.status === 'active' && (
                    <button 
                      className="execute-btn"
                      onClick={() => executeArbitrage(opportunity.id)}
                    >
                      Execute Arbitrage
                    </button>
                  )}
                  {opportunity.status === 'executing' && (
                    <button className="executing-btn" disabled>
                      Executing...
                    </button>
                  )}
                  {opportunity.status === 'completed' && (
                    <button className="completed-btn" disabled>
                      Completed
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profit Tracking Section */}
      <div className="arbitrage-profits">
        <h3>Arbitrage Profits</h3>
        <div className="profits-grid">
          <div className="profit-card">
            <div className="profit-icon">💰</div>
            <div className="profit-content">
              <div className="profit-value">${arbitrageProfits.totalProfit.toFixed(2)}</div>
              <div className="profit-label">Total Profit</div>
            </div>
          </div>
          <div className="profit-card">
            <div className="profit-icon">📅</div>
            <div className="profit-content">
              <div className="profit-value">${arbitrageProfits.dailyProfit.toFixed(2)}</div>
              <div className="profit-label">Daily Profit</div>
            </div>
          </div>
          <div className="profit-card">
            <div className="profit-icon">📊</div>
            <div className="profit-content">
              <div className="profit-value">{arbitrageProfits.executedTrades}</div>
              <div className="profit-label">Executed Trades</div>
            </div>
          </div>
          <div className="profit-card">
            <div className="profit-icon">🎯</div>
            <div className="profit-content">
              <div className="profit-value">{arbitrageProfits.successRate.toFixed(1)}%</div>
              <div className="profit-label">Success Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="arbitrage-stats">
        <h3>Arbitrage Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-value">{arbitrageOpportunities.length}</div>
              <div className="stat-label">Opportunities Found</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-value">
                ${arbitrageOpportunities.reduce((sum, opp) => sum + opp.profit, 0).toFixed(2)}
              </div>
              <div className="stat-label">Total Potential Profit</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <div className="stat-value">
                {arbitrageOpportunities.filter(opp => opp.status === 'completed').length}
              </div>
              <div className="stat-label">Executed Trades</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-content">
              <div className="stat-value">
                {arbitrageOpportunities.length > 0 
                  ? (arbitrageOpportunities.reduce((sum, opp) => sum + opp.profitPercent, 0) / arbitrageOpportunities.length).toFixed(2)
                  : 0}%
              </div>
              <div className="stat-label">Average Profit %</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Arbitrage;
